'use client';
import React, { useState, useCallback } from 'react';
import { PLAYER_COLORS } from '@/lib/gameEngine';
import type { PlayerConfig } from './GameSetupClient';
import { useGameAudio } from '@/hooks/useGameAudio';

type Props = {
  players: PlayerConfig[];
  onStart: (orderedIndices: number[]) => void;
  onBack: () => void;
};

type RollResult = {
  playerIndex: number;
  roll: number;
  rolling: boolean;
};

const DICE_FACES = ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];

function DiceFace({ value, rolling, color }: { value: number; rolling: boolean; color: string }) {
  return (
    <div
      className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl font-700 border-2 transition-all duration-300 ${
        rolling ? 'dice-roll-anim' : ''
      }`}
      style={{
        background: `${color}15`,
        borderColor: rolling ? color : `${color}40`,
        color,
        boxShadow: rolling ? `0 0 20px ${color}60` : 'none',
      }}
    >
      {rolling ? DICE_FACES[Math.floor(value * 5.99) % 6] : DICE_FACES[Math.min(value - 1, 5)]}
    </div>
  );
}

export default function DiceRollStep({ players, onStart, onBack }: Props) {
  const [rolls, setRolls] = useState<RollResult[]>(
    players.map((_, i) => ({ playerIndex: i, roll: 0, rolling: false }))
  );
  const [phase, setPhase] = useState<'initial' | 'rolling' | 'complete' | 'tiebreak'>('initial');
  const [tiedIndices, setTiedIndices] = useState<number[]>([]);
  const [isRolling, setIsRolling] = useState(false);
  const [finalOrder, setFinalOrder] = useState<number[]>([]);
  const { playDiceRoll } = useGameAudio();

  const rollDice = useCallback(() => {
    if (isRolling) return;
    setIsRolling(true);
    setPhase('rolling');
    playDiceRoll();

    // Animate rolling
    const rollTarget = tiedIndices.length > 0 ? tiedIndices : players.map((_, i) => i);

    // Set rolling state for all targets
    setRolls((prev) =>
      prev.map((r) =>
        rollTarget.includes(r.playerIndex) ? { ...r, rolling: true, roll: 1 } : r
      )
    );

    // Animate for 1.2s with random intermediate values
    let frame = 0;
    const animInterval = setInterval(() => {
      frame++;
      setRolls((prev) =>
        prev.map((r) =>
          rollTarget.includes(r.playerIndex)
            ? { ...r, roll: Math.floor(Math.random() * 6) + 1 }
            : r
        )
      );
      if (frame >= 8) {
        clearInterval(animInterval);

        // Final rolls
        const finalRolls = new Map<number, number>();
        rollTarget.forEach((idx) => {
          finalRolls.set(idx, Math.floor(Math.random() * 6) + 1);
        });

        setRolls((prev) =>
          prev.map((r) => ({
            ...r,
            roll: finalRolls.has(r.playerIndex) ? finalRolls.get(r.playerIndex)! : r.roll,
            rolling: false,
          }))
        );

        // Check for ties among rolled players
        const rolledValues = rollTarget.map((idx) => ({
          idx,
          val: finalRolls.get(idx) ?? 0,
        }));
        const maxVal = Math.max(...rolledValues.map((rv) => rv.val));
        const tied = rolledValues.filter((rv) => rv.val === maxVal).map((rv) => rv.idx);

        if (tied.length > 1) {
          setTiedIndices(tied);
          setPhase('tiebreak');
        } else {
          // Build final order: highest roll first
          const allRolls = rolls.map((r) => ({
            idx: r.playerIndex,
            val: finalRolls.get(r.playerIndex) ?? r.roll,
          }));
          const ordered = [...allRolls].sort((a, b) => b.val - a.val).map((r) => r.idx);
          setFinalOrder(ordered);
          setPhase('complete');
        }

        setIsRolling(false);
      }
    }, 120);
  }, [isRolling, players, tiedIndices, rolls]);

  const handleRerollTied = () => {
    setPhase('initial');
    setIsRolling(false);
    rollDice();
  };

  const getOrderedPlayers = () => {
    if (finalOrder.length > 0) return finalOrder;
    return [...rolls].sort((a, b) => b.roll - a.roll).map((r) => r.playerIndex);
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h2 className="text-hero-md text-foreground mb-3">Roll for Turn Order</h2>
        <p className="text-muted-foreground">
          Highest roll goes first. Tied players reroll until a winner is determined.
        </p>
      </div>

      {/* Player dice */}
      <div className="flex flex-col gap-3 mb-8">
        {players.map((player, index) => {
          const rollResult = rolls.find((r) => r.playerIndex === index);
          const color = PLAYER_COLORS[player.colorIndex].color;
          const isTied = tiedIndices.includes(index);
          const hasRolled = phase !== 'initial' && rollResult && rollResult.roll > 0;
          const orderPosition = phase === 'complete' ? getOrderedPlayers().indexOf(index) + 1 : null;

          return (
            <div
              key={`dice-player-${player.id}`}
              className={`flex items-center gap-4 p-4 rounded-2xl border transition-all duration-300 ${
                isTied && phase === 'tiebreak' ?'border-accent/60 bg-accent/10'
                  : phase === 'complete'&& orderPosition === 1 ?'border-primary/60 bg-primary/10' :'border-border bg-card'
              }`}
            >
              <div
                className="w-8 h-8 rounded-lg flex-shrink-0"
                style={{ background: color, boxShadow: `0 0 8px ${color}50` }}
              />
              <div className="flex-1">
                <div className="font-600 text-foreground text-sm">{player.name}</div>
                <div className="text-xs text-muted-foreground">
                  {player.isAI ? `AI — ${player.aiDifficulty}` : 'Human'}
                </div>
              </div>

              {hasRolled && rollResult && (
                <DiceFace value={rollResult.roll} rolling={rollResult.rolling} color={color} />
              )}

              {!hasRolled && (
                <div className="w-16 h-16 rounded-2xl border-2 border-dashed border-border flex items-center justify-center text-muted-foreground text-2xl">
                  ?
                </div>
              )}

              {phase === 'complete' && orderPosition && (
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-700 text-sm flex-shrink-0 ${
                    orderPosition === 1 ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {orderPosition}
                </div>
              )}

              {isTied && phase === 'tiebreak' && (
                <span className="text-xs font-600 text-accent px-2 py-1 rounded-lg bg-accent/20">Tied!</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Tie break notice */}
      {phase === 'tiebreak' && (
        <div className="mb-6 p-4 rounded-xl bg-accent/10 border border-accent/30">
          <div className="flex items-center gap-2 text-accent font-600 mb-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Tie Detected
          </div>
          <p className="text-sm text-muted-foreground">
            Tied players must reroll. Click Reroll to continue.
          </p>
        </div>
      )}

      {/* Turn order summary */}
      {phase === 'complete' && (
        <div className="mb-6 p-4 rounded-xl bg-primary/10 border border-primary/30">
          <div className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-3">
            Turn Order
          </div>
          <div className="flex flex-wrap gap-2">
            {getOrderedPlayers().map((idx, pos) => {
              const p = players[idx];
              const color = PLAYER_COLORS[p.colorIndex].color;
              return (
                <div
                  key={`order-${p.id}`}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-600"
                  style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}
                >
                  <span className="font-mono-game">{pos + 1}.</span>
                  {p.name}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-3.5 rounded-2xl border border-border text-foreground font-600 hover:bg-secondary/50 active:scale-95 transition-all duration-150"
        >
          Back
        </button>

        {phase === 'initial' && (
          <button
            onClick={rollDice}
            className="flex-grow py-3.5 rounded-2xl bg-primary text-primary-foreground font-700 hover:bg-primary/90 active:scale-95 transition-all duration-150 glow-primary flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Roll Dice
          </button>
        )}

        {phase === 'rolling' && (
          <button disabled className="flex-grow py-3.5 rounded-2xl bg-primary/50 text-primary-foreground font-700 cursor-not-allowed flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-primary-foreground/50 border-t-primary-foreground rounded-full animate-spin" />
            Rolling...
          </button>
        )}

        {phase === 'tiebreak' && (
          <button
            onClick={handleRerollTied}
            className="flex-grow py-3.5 rounded-2xl bg-accent text-accent-foreground font-700 hover:bg-accent/90 active:scale-95 transition-all duration-150 flex items-center justify-center gap-2"
          >
            Reroll Tied Players
          </button>
        )}

        {phase === 'complete' && (
          <button
            onClick={() => onStart(getOrderedPlayers())}
            className="flex-grow py-3.5 rounded-2xl bg-primary text-primary-foreground font-700 hover:bg-primary/90 active:scale-95 transition-all duration-150 glow-primary flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 5v10l7-5-7-5z" />
            </svg>
            Start Game!
          </button>
        )}
      </div>
    </div>
  );
}